/**
 * useTutorWaiting — l'élève attend son tuteur : « en avance », « pas encore là », « termine sa
 * séance précédente », le retard annoncé, les cinq minutes, puis à dix minutes la séance de
 * remplacement ou le rattrapage crédité.
 *
 * Le portage de `common/hooks/livekit/tutorNoShow/TutorNoShowContext.js` sans ses boîtes : la même
 * machine (`tutorNoShow/useTutorNoShowMachine`, déplacée telle quelle), les mêmes appels
 * (`recovery-possibilities`, `auto-recovery`, `auto-recovery/fallback`), la même présence du tuteur
 * gardée par séance (`tutorPresence`), le même « termine sa séance précédente » lu au relevé
 * (`live_signals.tutor_busy_elsewhere`). Les boîtes Abou deviennent l'état que l'écran pose : la
 * barre d'attente (`waiting`) et la feuille du remplacement (`dialog`).
 *
 * Comme aujourd'hui, seulement dans une séance de groupe qui sait remplacer un tuteur absent
 * (`supportsNoShowRecovery`), et seulement une fois l'élève dans la salle. Le tuteur est en retard
 * dans moins d'1 % des séances : ces états servent peu, ils doivent marcher.
 */
import moment from "moment"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../data/redux/actions/api"
import { cleanOldTutorPresence, setIsLiveModeEnabled, setTutorEverPresent } from "../../../data/redux/actions/liveMode"
import { liveRoomSignalsFor } from "../../../data/redux/reducers/liveMode"
import { authService } from "../../../data/services/authService"
import { liveService } from "../../../data/services/liveService"
import { apiCall, apiFailureOf, ENGINE_REQUEST_OPTIONS } from "../../shared/apiCall"
import { type EngineTelemetry } from "../../shared/telemetry"
import { announcedArrivalOf, sessionStartOf, type LiveSessionTimeLike } from "./liveSessionTime"
import { TUTOR_NO_SHOW } from "./tutorNoShow/constants"
import { isRetryableClaimRejection, useTutorNoShowMachine } from "./tutorNoShow/useTutorNoShowMachine"

/** La barre d'attente : pourquoi, l'heure annoncée, et le ton qui monte à cinq minutes. */
export type TutorWaiting = { reason: "early" | "absent" | "busy"; arrival?: string; late: boolean }

export type TutorAbsentStep = "proposal" | "confirmJoin" | "confirmLater" | "credited"

/** Le live de remplacement proposé (`possible_live_occurrences[0]`). */
export type ReplacementSession = {
    id?: number
    date?: string
    duration?: number
    tutor?: { id?: number; display_name?: string; avatar?: string | null } | null
} & Record<string, unknown>

export type TutorAbsentDialog = {
    step: TutorAbsentStep
    replacement: ReplacementSession | null
    creditReason: "declined" | "none"
    joining: boolean
}

export type TutorWaitingSession = LiveSessionTimeLike & {
    id?: number
    tutor_absence_dispatch_after_minutes?: number | null
}

export type UseTutorWaitingOptions = {
    liveSession: TutorWaitingSession | null
    /** La séance sait remplacer un tuteur absent (`supportsNoShowRecovery`). */
    enabled: boolean
    /** L'élève est dans la salle (connecté). */
    inRoom: boolean
    tutorPresent: boolean
    /** Le remplacement ou le crédit a dit au serveur que l'élève quitte : pas de `leaveCall` en plus. */
    skipLeaveRef: { current: boolean }
    /** Couper la salle avant de quitter le live. */
    disconnectRoom: () => Promise<void>
    telemetry: EngineTelemetry
}

export type TutorWaitingController = {
    waiting: TutorWaiting | null
    dialog: TutorAbsentDialog | null
    /** Un son pour attirer l'attention (la proposition, le crédit) : il change à chaque appel. */
    attention: number
    /** Ce que le serveur a répondu à une demande refusée (« trop tôt »…). */
    notice: string | null
    dismissNotice: () => void
    join: () => void
    decline: () => void
    back: () => void
    confirmJoin: () => void
    confirmLater: () => void
}

type RootState = { data?: { tutorPresence?: { history?: Record<string, unknown> } } }

/** Le délai avant de quitter le live après un remplacement ou un crédit, celui d'aujourd'hui. */
const LEAVE_DELAY_MS = 400

type ClaimFailure = { claim_rejected_reason?: string; retry_after_seconds?: number; message?: string }

export function useTutorWaiting(options: UseTutorWaitingOptions): TutorWaitingController {
    const { liveSession, enabled, inRoom, tutorPresent, skipLeaveRef, disconnectRoom, telemetry } = options
    const dispatch = useDispatch()
    const sessionStart = useMemo(() => sessionStartOf(liveSession), [liveSession])
    const liveSessionId = liveSession?.id
    // La clé de la présence et la date de l'occurrence : relues quand la séance change, pas à chaque rendu.
    const { sessionKey, occurrenceDate } = useMemo(() => {
        const start = sessionStart != null ? moment(sessionStart) : null
        return {
            sessionKey:
                liveSessionId != null && start != null ? `${liveSessionId}_${start.format("YYYY-MM-DD-HH:mm")}` : null,
            occurrenceDate: start != null ? start.format("YYYY-MM-DD HH:mm") : undefined,
        }
    }, [liveSessionId, sessionStart])
    const hasEverBeenPresent = useSelector(
        (state: RootState) => sessionKey != null && !!state.data?.tutorPresence?.history?.[sessionKey]
    )
    const busyElsewhere = useSelector(
        (state: unknown) => !!liveRoomSignalsFor(state, liveSession?.id ?? null)?.tutor_busy_elsewhere
    )

    const setTutorPresent = useCallback(() => {
        if (sessionKey != null) dispatch(setTutorEverPresent(sessionKey) as never)
    }, [sessionKey, dispatch])

    useEffect(() => {
        if (enabled) dispatch(cleanOldTutorPresence() as never)
    }, [enabled, dispatch])

    const machine = useTutorNoShowMachine({
        sessionStartTime: sessionStart,
        isTutorPresent: tutorPresent,
        isStudentInSession: inRoom,
        enabled: enabled && inRoom,
        hasEverBeenPresent,
        setTutorPresent,
        dispatchAfterMinutes: liveSession?.tutor_absence_dispatch_after_minutes,
    })
    const { actions } = machine

    const [step, setStep] = useState<"proposal" | "confirmJoin" | "confirmLater">("proposal")
    const [attention, setAttention] = useState(0)
    const [notice, setNotice] = useState<string | null>(null)
    const searchTriggeredRef = useRef(false)
    // Un remplacement ou un crédit à la fois : le bouton reste actif pendant la requête, et chaque
    // appui en envoyait une de plus (quatre crédits pour une séance, 27/10/2026).
    const claimInFlightRef = useRef(false)

    const search = useCallback(() => {
        if (liveSession?.id == null || occurrenceDate == null) {
            telemetry.transition("waiting", "credited", "no_occurrence")
            actions.noRecoveryFound()
            return
        }
        apiCall<{ possible_live_occurrences?: ReplacementSession[] }>(
            dispatch as never,
            liveService.getRecoveryPossibilities,
            {
                urlParams: {
                    live_session_id: liveSession.id,
                    occurrence_date: occurrenceDate,
                    search_min_date: moment().format("YYYY-MM-DD HH:mm"),
                    search_max_date: moment().add(TUTOR_NO_SHOW.SEARCH_WINDOW, "minutes").format("YYYY-MM-DD HH:mm"),
                },
            }
        )
            .then((data) => {
                const found = data?.possible_live_occurrences?.[0]
                telemetry.transition("searching", found != null ? "proposing" : "crediting", "recovery_possibilities")
                if (found == null) actions.noRecoveryFound()
                else actions.recoveryFound(found)
            })
            .catch((cause: unknown) => {
                telemetry.error("recovery_search", cause)
                actions.reportError(cause)
            })
    }, [liveSession?.id, occurrenceDate, dispatch, actions, telemetry])

    // La recherche part au seuil du serveur (ou à dix minutes), une fois ; elle se réarme quand le
    // tuteur arrive ou qu'un refus « trop tôt » renvoie la machine en attente.
    useEffect(() => {
        if (!enabled) return
        if (machine.shouldTriggerSearch && !searchTriggeredRef.current) {
            searchTriggeredRef.current = true
            actions.startSearch()
            search()
        } else if (machine.status === "IDLE" || machine.status === "WAITING") {
            searchTriggeredRef.current = false
        }
    }, [enabled, machine.shouldTriggerSearch, machine.status, actions, search])

    // La proposition et le crédit s'annoncent par un son, comme aujourd'hui.
    useEffect(() => {
        if (machine.isProposing && machine.recoverySession != null) setAttention((count) => count + 1)
    }, [machine.isProposing, machine.recoverySession])
    useEffect(() => {
        if (machine.isCrediting) setAttention((count) => count + 1)
    }, [machine.isCrediting])
    useEffect(() => {
        if (machine.isProposing) setStep("proposal")
    }, [machine.isProposing])

    const leaveLive = useCallback(
        (refreshAccount: boolean) => {
            const leave = () => {
                setTimeout(() => {
                    dispatch(setIsLiveModeEnabled(false) as never)
                    // Relu, le compte montre la nouvelle séance.
                    if (refreshAccount)
                        dispatch(
                            apiRequest(authService.getUser, {}, {}, {}, null, null, ENGINE_REQUEST_OPTIONS) as never
                        )
                }, LEAVE_DELAY_MS)
            }
            disconnectRoom()
                .catch((cause: unknown) => {
                    telemetry.error("leave_after_recovery", cause)
                })
                .finally(leave)
        },
        [dispatch, disconnectRoom, telemetry]
    )

    const onClaimRejected = useCallback(
        (failure: ClaimFailure) => {
            if (isRetryableClaimRejection(failure)) skipLeaveRef.current = false
            actions.claimRejected(failure)
            if (failure.message) setNotice(failure.message)
        },
        [actions, skipLeaveRef]
    )

    const failureOf = (cause: unknown): (ClaimFailure & { status?: number | null }) | null =>
        apiFailureOf(cause) as (ClaimFailure & { status?: number | null }) | null

    const confirmJoin = useCallback(() => {
        if (claimInFlightRef.current) return
        claimInFlightRef.current = true
        // Le drapeau AVANT l'appel : le démontage qui suit ne doit pas dire au serveur qu'on quitte.
        skipLeaveRef.current = true
        actions.joinRecovery()
        apiCall(dispatch as never, liveService.autoRecoveryTutorLate, {
            routeParams: { id: liveSession?.id },
            data: { recovery_occurrence_id: machine.recoverySession?.id, original_occurrence_date: occurrenceDate },
        })
            .then(() => {
                telemetry.transition("joining", "done", "auto_recovery")
                actions.complete()
                leaveLive(true)
            })
            .catch((cause: unknown) => {
                const failure = failureOf(cause)
                if (failure?.claim_rejected_reason) {
                    onClaimRejected(failure)
                    return
                }
                if (failure?.message === "SLOT_NO_LONGER_AVAILABLE" || failure?.status === 409) {
                    // Le live proposé s'est rempli : une nouvelle recherche.
                    telemetry.transition("joining", "searching", "slot_unavailable")
                    searchTriggeredRef.current = false
                    actions.startSearch()
                    search()
                    return
                }
                telemetry.error("auto_recovery", cause)
                actions.reportError(cause)
            })
            .finally(() => {
                claimInFlightRef.current = false
            })
    }, [
        skipLeaveRef,
        actions,
        dispatch,
        liveSession?.id,
        machine.recoverySession?.id,
        occurrenceDate,
        telemetry,
        leaveLive,
        onClaimRejected,
        search,
    ])

    const confirmLater = useCallback(() => {
        if (!machine.isCrediting) {
            // « Rattraper plus tard » confirmé : le crédit, comme aujourd'hui, se confirme une seconde fois.
            actions.preferCredit()
            return
        }
        if (claimInFlightRef.current) return
        claimInFlightRef.current = true
        skipLeaveRef.current = true
        apiCall(dispatch as never, liveService.autoRecoveryFallback, {
            routeParams: { id: liveSession?.id },
            data: { original_occurrence_date: occurrenceDate },
        })
            .then(() => {
                telemetry.transition("crediting", "done", "auto_recovery_fallback")
                actions.complete()
                leaveLive(false)
            })
            .catch((cause: unknown) => {
                const failure = failureOf(cause)
                if (failure?.claim_rejected_reason) {
                    onClaimRejected(failure)
                    return
                }
                telemetry.error("auto_recovery_fallback", cause)
                actions.reportError(cause)
            })
            .finally(() => {
                claimInFlightRef.current = false
            })
    }, [
        machine.isCrediting,
        actions,
        skipLeaveRef,
        dispatch,
        liveSession?.id,
        occurrenceDate,
        telemetry,
        leaveLive,
        onClaimRejected,
    ])

    // L'attente se dit dès que la machine attend, en avance comprise (« Tu es en avance ! ») : l'ancienne
    // boîte ne s'ouvrait qu'à partir du début (`INITIAL_DIALOG_DELAY`), si bien que son texte « en
    // avance » ne pouvait jamais s'afficher.
    const waitingStatus = machine.status === "WAITING"
    const arrival = enabled && waitingStatus && !busyElsewhere ? announcedArrivalOf(liveSession) : null
    const waiting: TutorWaiting | null =
        enabled && waitingStatus
            ? {
                  // Vu en train de finir sa séance d'avant : cela prime sur un retard annoncé plus tôt.
                  reason: machine.isEarly ? "early" : busyElsewhere ? "busy" : "absent",
                  ...(arrival != null ? { arrival } : {}),
                  late: machine.shouldShowBanner,
              }
            : null

    const dialog: TutorAbsentDialog | null = !enabled
        ? null
        : machine.isJoining
          ? { step: "confirmJoin", replacement: machine.recoverySession, creditReason: "none", joining: true }
          : machine.isProposing && machine.recoverySession != null
            ? { step, replacement: machine.recoverySession, creditReason: "none", joining: false }
            : machine.isCrediting
              ? {
                    step: "credited",
                    replacement: null,
                    creditReason: machine.declinedByUser ? "declined" : "none",
                    joining: false,
                }
              : null

    return {
        waiting,
        dialog,
        attention,
        notice,
        dismissNotice: () => setNotice(null),
        join: () => setStep("confirmJoin"),
        decline: () => setStep("confirmLater"),
        back: () => setStep("proposal"),
        confirmJoin,
        confirmLater,
    }
}
